import { PlanKind, type PlanRequirement } from "@glide/common-core/dist/js/billing-vnext/subscriptions";
import type { ActionProps, PluginProps, PluginTier, PluginTierList } from "@glide/plugins";
import { assertNever } from "@glideapps/ts-necessities";
import { BillingPlan } from "@glide/billing-types";
import { getFeatureSetting, hasRequiredEntitlementsForPlugin } from "@glide/common-core";
import type { AppDescriptionContext } from "@glide/function-utils";

export const getRequiredPlan = (
    pluginTiers: PluginTierList | undefined,
    appTier: PluginTier
): PlanRequirement | undefined => {
    if (pluginTiers === undefined) {
        return undefined;
    }
    if (pluginTiers[0] === "contact-sales") {
        return "contact-sales";
    }
    const planKind = tierToPlanKind(pluginTiers[0]);
    return pluginTiers.includes(appTier) ? undefined : planKind;
};

export function getRequiredTierForPlugin(
    plugin: PluginProps,
    action: ActionProps | undefined,
    adc: AppDescriptionContext
): PluginTierList | undefined {
    if (action?.tier !== undefined) {
        return action.tier;
    }

    // Plugins that are explicitly allowed don't require a tier
    if (getFeatureSetting("unifiedPluginEminenceChecks")) {
        if (hasRequiredEntitlementsForPlugin(adc.appID, plugin.id, plugin.tier, adc.eminenceFlags)) {
            return undefined;
        }
    } else {
        if (adc.eminenceFlags.allowedPlugins.includes(plugin.id)) return undefined;
    }

    return plugin.tier;
}

export function getRequiredPlanForPlugin(
    plugin: PluginProps,
    action: ActionProps | undefined,
    adc: AppDescriptionContext
): PlanRequirement | undefined {
    const requiredTier = getRequiredTierForPlugin(plugin, action, adc);
    return getRequiredPlan(requiredTier, adc.eminenceFlags.pluginTier);
}

export function hasRequiredPlan(requiredTiers: PluginTierList | undefined, tier: PluginTier): boolean {
    if (requiredTiers === undefined) return false;
    return requiredTiers.includes(tier);
}

export function tierToPlanKind(tier: PluginTier): PlanKind {
    switch (tier) {
        case "free":
            return PlanKind.Free;
        case "starter":
            return PlanKind.V3Starter;
        case "education":
            return PlanKind.V3NonProfit;
        case "pro":
            return PlanKind.V3Pro;
        case "business":
            return PlanKind.V3Business;
        case "enterprise":
            return PlanKind.Unlimited;
        case "contact-sales":
            return PlanKind.Free;
        default:
            assertNever(tier);
    }
}

export function tierToPlanRequirement(tier: PluginTier): PlanRequirement {
    if (tier === "contact-sales") {
        return "contact-sales";
    }
    return tierToPlanKind(tier);
}

export function planKindToBillingPlan(planKind: PlanKind): BillingPlan | undefined {
    switch (planKind) {
        case PlanKind.V3Starter:
            return BillingPlan.Starter;
        case PlanKind.V3Pro:
            return BillingPlan.Pro;
        case PlanKind.V3Business:
            return BillingPlan.Business;
        case PlanKind.Unlimited:
            return BillingPlan.Enterprise;
        case PlanKind.TestPro:
            return BillingPlan.Pro;
        case PlanKind.V3EnterpriseBaseNoUP:
            return BillingPlan.Enterprise;
        case PlanKind.Free:
        case PlanKind.V3NonProfit:
            return undefined;
        default:
            assertNever(planKind);
    }
}

export function tierToBillingPlan(tier: PluginTier): BillingPlan | undefined {
    return planKindToBillingPlan(tierToPlanKind(tier));
}
